import React, { useCallback, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { isWithinInterval } from 'date-fns';
import clsx, { ClassArray } from 'clsx';
import { VacationAppProps } from '../../../../store/vacations/VacationsUtils';
import { DateRangePickerValueType } from '../../../../common/components/ui/DatePicker/DateRangePicker';
import {
  checkDatesIncludeDate,
  getDateUtc,
  isoStringToMilliseconds,
  parseIsoToLocal
} from '../../../../utils/date/date';
import { useWindowSize } from '../../../../common/hooks/useWindowSize';
import { BottomSheet } from '../../../../common/components/ui/BottomSheet/BottomSheet';
import { ReactComponent as CalendarIcon } from '../../../../assets/img/icons/calendar.svg';
import {
  Button,
  ButtonSize,
  ButtonVariant
} from '../../../../common/components/ui/Button';
import {
  Calendar,
  CalendarProps
} from '../../../../common/components/ui/Calendar/Calendar';
import {
  VacationApplicationType,
  VacationStatusesEnum
} from '../../../../graphql/types';
import s from './VacationAppForm.module.scss';

export interface VacationAppFormCalendarProps
  extends FormikProps<VacationAppProps>,
    Pick<CalendarProps, 'minDate' | 'maxDate' | 'excludeDates'> {
  daysAvailable: number | null;
  holidayDates: Date[];
  vacationApps?: VacationApplicationType[];
  isVacationsDisabled: boolean;
}

/**
 * Возвращает boolean, находится ли переданная дата среди периодов переданного заявления на отпуск
 * @param {Date} date
 * @param {VacationApplicationType} vacationApplication
 * @returns {Boolean}
 */
const isWithinPeriods = (
  date: Date,
  vacationApplication: VacationApplicationType
) => {
  if (!vacationApplication.vacation_schedule_periods) {
    return false;
  }

  const vacationSchedulePeriods = JSON.parse(
    vacationApplication.vacation_schedule_periods
  );
  const replacePlans = vacationSchedulePeriods.replace_plans;
  const newPlans = vacationSchedulePeriods.new_plans;

  const periods: Array<{ start: Date; end: Date }> = [];
  if (replacePlans && replacePlans.length) {
    replacePlans.forEach((replacePlan) => {
      const newPlans = replacePlan.new_plans;
      if (newPlans && newPlans.length) {
        newPlans.forEach((newPlan) => {
          if (newPlan.start && newPlan.end) {
            periods.push({
              start: new Date(getDateUtc(newPlan.start)),
              end: new Date(getDateUtc(newPlan.end))
            });
          }
        });
      }
    });
  } else if (newPlans && newPlans.length) {
    newPlans.forEach((newPlan) => {
      if (newPlan.start && newPlan.end) {
        periods.push({
          start: new Date(getDateUtc(newPlan.start)),
          end: new Date(getDateUtc(newPlan.end))
        });
      }
    });
  }

  if (periods && periods.length) {
    let isWithin = false;

    periods.forEach((period) => {
      if (isWithinInterval(date, { start: period.start, end: period.end })) {
        isWithin = true;
        return;
      }
    });

    return isWithin;
  }

  return false;
};

export const VacationAppFormCalendar: React.FC<VacationAppFormCalendarProps> = ({
  values,
  setFieldValue,
  daysAvailable,
  holidayDates,
  vacationApps,
  isVacationsDisabled,
  ...props
}) => {
  const { isMobile } = useWindowSize();
  const [showCalendar, setShowCalendar] = useState(false);

  const calendarValue: DateRangePickerValueType = useMemo(() => {
    return [
      parseIsoToLocal(values.start ?? ''),
      parseIsoToLocal(values.end ?? '')
    ];
  }, [values]);

  const sentForApprovalVacationApps = useMemo(() => {
    if (!vacationApps?.length) return [];
    return vacationApps.filter(
      (app) => app.status === VacationStatusesEnum.SentForApproval
    );
  }, [vacationApps]);

  const approvedVacationApps = useMemo(() => {
    if (!vacationApps?.length) return [];
    return vacationApps.filter(
      (app) => app.status === VacationStatusesEnum.Approved
    );
  }, [vacationApps]);

  const completeVacationApps = useMemo(() => {
    if (!vacationApps?.length) return [];
    return vacationApps.filter(
      (app) => app.status === VacationStatusesEnum.Complete
    );
  }, [vacationApps]);

  const handleSetDayClassName = useCallback(
    (date: Date) => {
      const classNames: ClassArray = [];

      if (checkDatesIncludeDate(holidayDates, date)) {
        classNames.push(s.Calendar__day_holiday);
      }

      sentForApprovalVacationApps.forEach((app) => {
        const start = isoStringToMilliseconds(parseIsoToLocal(app.start));
        const end = isoStringToMilliseconds(parseIsoToLocal(app.end));
        const dateInMilliseconds = isoStringToMilliseconds(
          parseIsoToLocal(date.toISOString())
        );
        if (isWithinInterval(dateInMilliseconds, { start, end })) {
          classNames.push(s.Calendar__day_sentForApprovalVacationApp);
        }

        if (app.vacation_schedule_periods) {
          const isWithin = isWithinPeriods(date, app);
          if (isWithin) {
            classNames.push(s.Calendar__day_sentForApprovalVacationApp);
          }
        }
      });
      approvedVacationApps.forEach((app) => {
        const start = isoStringToMilliseconds(parseIsoToLocal(app.start));
        const end = isoStringToMilliseconds(parseIsoToLocal(app.end));
        const dateInMilliseconds = isoStringToMilliseconds(
          parseIsoToLocal(date.toISOString())
        );
        if (isWithinInterval(dateInMilliseconds, { start, end })) {
          classNames.push(s.Calendar__day_approvedVacationApp);
        }

        if (app.vacation_schedule_periods) {
          const isWithin = isWithinPeriods(date, app);
          if (isWithin) {
            classNames.push(s.Calendar__day_approvedVacationApp);
          }
        }
      });
      completeVacationApps.forEach((app) => {
        const start = isoStringToMilliseconds(parseIsoToLocal(app.start));
        const end = isoStringToMilliseconds(parseIsoToLocal(app.end));
        const dateInMilliseconds = isoStringToMilliseconds(
          parseIsoToLocal(date.toISOString())
        );
        if (isWithinInterval(dateInMilliseconds, { start, end })) {
          classNames.push(s.Calendar__day_completeVacationApp);
        }
        if (app.vacation_schedule_periods) {
          const isWithin = isWithinPeriods(date, app);
          if (isWithin) {
            classNames.push(s.Calendar__day_completeVacationApp);
          }
        }
      });

      return clsx(...classNames);
    },
    [
      holidayDates,
      sentForApprovalVacationApps,
      approvedVacationApps,
      completeVacationApps
    ]
  );

  const handleChange = useCallback(
    (values) => {
      setFieldValue('start', values[0]);
      setFieldValue('end', values[1]);
    },
    [setFieldValue]
  );

  const handleReset = useCallback(() => {
    setFieldValue('start', '');
    setFieldValue('end', '');
  }, [setFieldValue]);

  const calendar = (
    <Calendar
      classes={{
        title: s.VacationAppFormCalendar__title
      }}
      dayClassName={handleSetDayClassName}
      title={!isMobile ? 'Календарь' : ''}
      value={calendarValue}
      disabled={!daysAvailable || isVacationsDisabled}
      onReset={handleReset}
      onChange={handleChange}
      {...props}
    />
  );

  return isMobile ? (
    <>
      <Button
        className={s.VacationAppFormCalendar__calendarButton}
        variant={showCalendar ? ButtonVariant.primary : ButtonVariant.secondary}
        size={ButtonSize.large}
        iconLeft={<CalendarIcon />}
        disabled={!daysAvailable || isVacationsDisabled}
        onClick={() => setShowCalendar(true)}
      >
        Календарь
      </Button>
      <BottomSheet
        title={'Календарь'}
        open={showCalendar}
        onDismiss={() => setShowCalendar(false)}
        snapPoints={({ minHeight }) => minHeight}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}
        >
          {calendar}
        </div>
      </BottomSheet>
    </>
  ) : (
    calendar
  );
};
